
import { PARegistry } from "../singletons/pa-registry";
import type { PAOperation } from "./operation";
import type { PATechnician } from "./technician";
import type { PATechnicianDate } from "./technician-date";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import { PANotifications } from "./pa-notifications";

export interface ChangedTechnicianTimestamps {
  changed_technician: PATechnician;
  changed_timestamps: number[] | 'all';
}

export class PAPlanningInstructions extends FireUpdateOnChange<ChangedTechnicianTimestamps[] | 'all'>{

  executeBeforeChange(): void {
  }

  operationConstraintMap = new Map<number, TechnicianConstraint[]>()

  constructor() {
    super()
    this.subscribeToChanges(() => {
      console.log('Updated Subject')
    })
  }

  addOperationsTechnicianConstraints(operations: PAOperation[], technicians: PATechnician[], timestamps?: number[]) {
    const throw_sla_warnings_for_operations: PAOperation[] = []
    const throw_appointment_errors_for_operations: PAOperation[] = []
    let throw_warnings: string[] = []

    const changed_technician_timestamps: ChangedTechnicianTimestamps[] = []
    for (const operation of operations) {
      if (!this.operationConstraintMap.has(operation.id)) {
        this.operationConstraintMap.set(operation.id, [])
      }
      const t_constraints = this.operationConstraintMap.get(operation.id)
      const not_added_technicians = technicians.filter(t => !t_constraints.find(tc => tc.technician == t))
      this.operationConstraintMap.set(operation.id, t_constraints.concat(not_added_technicians.map(t => {
        return {technician: t, day_timestamps: null}
      })))

      not_added_technicians.map(t => changed_technician_timestamps.push({
        changed_technician: t,
        changed_timestamps: timestamps || 'all'
      }))

      if (timestamps) {
        const doable_timestamps = timestamps.filter(ts => operation.checkDayTimestampDoability(ts))
        if (timestamps.find(ts => !operation.checkSLADoability(ts))) throw_sla_warnings_for_operations.push(operation)
        if (timestamps.find(ts => !operation.checkAppointmentDoability(ts))) throw_appointment_errors_for_operations.push(operation)

        for (const technician of technicians) {
          throw_warnings = throw_warnings.concat(operation.nonFeasibilityReasonsForTechnician(technician))
          const t_constraint = this.getOperationsTechnicianConstraint(operation, technician)
          if (!t_constraint.day_timestamps) {
            t_constraint.day_timestamps = doable_timestamps
            if (!not_added_technicians.includes(technician)) {
              changed_technician_timestamps.push({changed_technician: technician, changed_timestamps: doable_timestamps})
            }
          } else {
            const add_timestamps: number[] = []
            for (const timestamp of doable_timestamps) {
              if (!t_constraint.day_timestamps.includes(timestamp)) {
                add_timestamps.push(timestamp)
              }
            }
            t_constraint.day_timestamps = t_constraint.day_timestamps.concat(add_timestamps)
            if (!not_added_technicians.includes(technician)) {
              changed_technician_timestamps.push({changed_technician: technician, changed_timestamps: add_timestamps})
            }
          }
        }
      }
    }
    this.update(changed_technician_timestamps)

    this.handleSnackbarMessage(throw_sla_warnings_for_operations, throw_appointment_errors_for_operations, throw_warnings);
  }

  private handleSnackbarMessage(throw_sla_warnings_for_operations: PAOperation[], throw_appointment_errors_for_operations: PAOperation[], throw_warnings: string[]) {
    const sla_error_messages = throw_sla_warnings_for_operations.length ? ('SLA-Aufträge sollten nur vor dem SLA-Datum geplant werden: \n' + throw_sla_warnings_for_operations.map(op => `${op.id} - SLA:${op.slaDateInformation()}`).join('\n')) : ''
    const appointment_error_messages = throw_appointment_errors_for_operations.length ? ('Aufträge können nur am Termin-Datum geplant werden: \n' + throw_appointment_errors_for_operations.map(op => `${op.id} - TER:${op.appointmentDateInformation()}`).join('\n')) : ''
    let error_message = [sla_error_messages, appointment_error_messages].filter(err => err).join('\n')
    error_message = error_message ? 'Fehler:\n' + error_message : error_message
    const warning_message = throw_warnings.length ? 'Warnungen:\n' + [...new Set(throw_warnings)].join('\n') : ''

    if (error_message || warning_message) {
      PANotifications.open(
        [error_message, warning_message].filter(m => m).join('\n'),
        'Ok',
        {type: 'error', position: 'top'}
      ).dismissAfter(10000)
    }
  }

  removeOperationsTechnicianConstraints(operations: PAOperation[], technicians: PATechnician[], timestamps?: number[]) {
    const changed_technician_timestamps: ChangedTechnicianTimestamps[] = []
    for (const operation of operations) {
      if (this.operationConstraintMap.has(operation.id)) {
        const t_constraints = this.operationConstraintMap.get(operation.id)
        if (timestamps) {
          for (const technician of technicians) {
            const t_constraint = this.getOperationsTechnicianConstraint(operation, technician)
            if (t_constraint?.day_timestamps) {
              const filtered_timestamps = t_constraint.day_timestamps.filter(ts => timestamps.includes(ts))
              changed_technician_timestamps.push({
                changed_technician: technician,
                changed_timestamps: filtered_timestamps
              })
              t_constraint.day_timestamps = t_constraint.day_timestamps.filter(ts => !timestamps.includes(ts))
              if (!t_constraint.day_timestamps.length) {
                this.operationConstraintMap.set(operation.id, t_constraints.filter(c => c.technician != technician))
              }
            }
          }
        } else {
          const removed_technician_constraints = t_constraints.filter(tc => technicians.includes(tc.technician))
          removed_technician_constraints.map(tc => changed_technician_timestamps.push({
            changed_technician: tc.technician,
            changed_timestamps: tc.day_timestamps || 'all'
          }))
          this.operationConstraintMap.set(operation.id, t_constraints.filter(tc => !technicians.includes(tc.technician)))
        }
      }
    }
    this.update(changed_technician_timestamps)
  }

  getOperationsTechnicianConstraint(operation: PAOperation, technician: PATechnician): TechnicianConstraint | undefined {
    if (this.operationConstraintMap.has(operation.id)) {
      return this.operationConstraintMap.get(operation.id).find(tc => tc.technician == technician)
    }
  }

  update(changed_technician_timestamps?: ChangedTechnicianTimestamps[]) {
    if (changed_technician_timestamps) {
      this.fireUpdate(changed_technician_timestamps)
    } else {
      this.fireUpdate('all')
    }
  }

  resetPlanningInstructions(skip_update?: boolean) {
    this.operationConstraintMap = new Map<number, TechnicianConstraint[]>()
    if (!skip_update) this.update()
  }

  operationIsDoableOnTechnicianDate(operation: PAOperation, technician_date: PATechnicianDate): boolean {

    if (technician_date.isPastTechnicianDate) {
      return false
    }

    if (!this.operationConstraintMap.has(operation.id) || !this.operationConstraintMap.get(operation.id).length) {
      return false
    } else {
      const technician_constraint = this.getOperationsTechnicianConstraint(operation, technician_date.technician)
      if (technician_constraint) {
        const cdts = technician_constraint.day_timestamps
        return !cdts || cdts.includes(technician_date.day.utc_timestamp);
      } else {
        return false
      }
    }
  }

}

export interface TechnicianConstraint {
  technician: PATechnician,
  day_timestamps?: number[]
}
